import type { FormField, TableRowDef } from "@/lib/database";

import { evaluateConditions } from "@/lib/fields/visibility";

/**
 * Хелперы поля «Таблица» (t_82191af7).
 * Значение таблицы в formData/submission_data:
 * - динамический режим (строки добавляет респондент): массив объектов-строк
 *   [{ colId: value, ... }, ...];
 * - фиксированный режим (в настройках заданы tableFixedRows): объект
 *   { rowId: { colId: value, ... }, ... } по фиксированным строкам.
 */

export type TableRowValue = Record<string, any>;

/** Пустое значение ячейки (false у checkbox — заполненное значение). */
export function isCellValueEmpty(value: any): boolean {
	if (value === undefined || value === null) return true;
	if (typeof value === "string") return value.trim() === "";
	if (Array.isArray(value)) return value.length === 0;
	return false;
}

/** Строка пуста, если пусты все ячейки по всем колонкам таблицы. */
export function isEmptyTableRow(
	row: TableRowValue,
	columns: FormField[],
): boolean {
	if (!row || typeof row !== "object") return true;
	return columns.every((col) => isCellValueEmpty(row[col.id]));
}

/** Создать пустую строку (все ячейки пустые). */
export function createEmptyTableRow(): TableRowValue {
	return {};
}

/** Фиксированный ли режим (заданы ли фиксированные строки в настройках). */
export function isTableFixed(field: FormField): boolean {
	return (field.settings?.tableFixedRows?.length ?? 0) > 0;
}

/** Фиксированные строки таблицы (вертикальная ориентация). */
export function getTableFixedRows(field: FormField): TableRowDef[] {
	return field.settings?.tableFixedRows || [];
}

export interface TableRowEntry {
	rowId?: string;
	rowLabel?: string;
	cells: TableRowValue;
}

/** Канонический список строк таблицы независимо от режима. */
export function getTableRowEntries(
	field: FormField,
	value: any,
): TableRowEntry[] {
	const fixedRows = field.settings?.tableFixedRows || [];
	const columns = field.settings?.tableColumns || [];
	if (fixedRows.length > 0) {
		return fixedRows.map((r) => ({
			rowId: r.id,
			rowLabel: r.label,
			cells:
				(value &&
					typeof value === "object" &&
					!Array.isArray(value) &&
					value[r.id] &&
					typeof value[r.id] === "object" &&
					!Array.isArray(value[r.id]) &&
					value[r.id]) ||
				{},
		}));
	}
	return normalizeTableRows(value, columns).map((cells) => ({ cells }));
}

/** Колонки, видимые хотя бы в одной строке (условная логика колонок). */
export function getVisibleColumns(
	field: FormField,
	entries: TableRowEntry[],
): FormField[] {
	return (field.settings?.tableColumns || []).filter((col: any) => {
		const logic = col.settings?.conditionalVisibility;
		if (!logic?.enabled) {
			return true;
		}
		return entries.some(
			(entry) =>
				!logic.conditions.length ||
				evaluateConditions(logic, entry.cells as Record<string, any>),
		);
	});
}

/**
 * Нормализовать значение таблицы (динамический режим): гарантирует массив
 * объектов-строк, выбрасывает мусор (не-объекты) и обрезает пустые строки
 * с КОНЦА (пустые строки в середине оставляем — пользователь мог удалить
 * значение случайно). Используется перед отправкой и на сервере.
 */
export function normalizeTableRows(
	value: any,
	columns: FormField[],
): TableRowValue[] {
	if (!Array.isArray(value)) return [];
	const rows = value.filter(
		(row) => row && typeof row === "object" && !Array.isArray(row),
	);
	while (rows.length > 0 && isEmptyTableRow(rows[rows.length - 1], columns)) {
		rows.pop();
	}
	return rows;
}

/** Нормализовать значение фиксированной таблицы: { rowId: cells } со всеми строками. */
export function normalizeFixedTableValue(
	value: any,
	fixedRows: TableRowDef[],
): Record<string, TableRowValue> {
	const out: Record<string, TableRowValue> = {};
	for (const row of fixedRows) {
		const cells =
			value && typeof value === "object" && !Array.isArray(value)
				? value[row.id]
				: undefined;
		out[row.id] =
			cells && typeof cells === "object" && !Array.isArray(cells) ? cells : {};
	}
	return out;
}

export type TableValidationError =
	| { code: "tableRequired" }
	| { code: "columnRequired"; columnLabel: string }
	| { code: "rowRequired"; rowLabel: string };

/**
 * Валидация значения таблицы (оба режима, t_82191af7):
 * - required таблицы = минимум 1 непустая строка;
 * - required колонки = каждая непустая строка обязана иметь значение;
 * - required фиксированной строки = строка обязана быть заполнена.
 * Условно скрытые колонки не валидируются (см. getVisibleColumns).
 */
export function validateTableValue(
	field: FormField,
	value: any,
): TableValidationError | undefined {
	const entries = getTableRowEntries(field, value);
	const visibleColumns = getVisibleColumns(field, entries);
	const nonEmpty = entries.filter((entry) =>
		!isEmptyTableRow(entry.cells, visibleColumns),
	);
	if (field.required && nonEmpty.length === 0) {
		return { code: "tableRequired" };
	}
	for (const entry of nonEmpty) {
		for (const col of visibleColumns) {
			if (col.required && isCellValueEmpty(entry.cells[col.id])) {
				return { code: "columnRequired", columnLabel: col.label };
			}
		}
	}
	for (const entry of entries) {
		const row = entry.rowId
			? (field.settings?.tableFixedRows || []).find((r) => r.id === entry.rowId)
			: undefined;
		if (row?.required && isEmptyTableRow(entry.cells, visibleColumns)) {
			return { code: "rowRequired", rowLabel: row.label };
		}
	}
	return undefined;
}

/** Приведение значения ячейки к типу колонки при TSV-вставке. */
export function coerceCellForColumn(col: FormField, raw: string): any {
	const v = raw.trim();
	if (v === "") return "";
	switch (col.type) {
		case "number": {
			const n = Number(v.replace(",", "."));
			return Number.isFinite(n) ? n : "";
		}
		case "checkbox":
			return ["x", "1", "true", "да", "yes", "+"].includes(v.toLowerCase());
		case "select": {
			const opts = (col.options || []).map((o) =>
				typeof o === "string" ? o : (o?.value ?? ""),
			);
			return (
				opts.find((o) => String(o).toLowerCase() === v.toLowerCase()) ?? ""
			);
		}
		default:
			return v;
	}
}

/**
 * Разобрать TSV (вставка из Excel/Google Sheets) в строки таблицы.
 * Лишние сверх maxRows строки отбрасываются. Возвращает также число
 * отброшенных строк для уведомления пользователя.
 */
export function parseTsvRows(
	tsv: string,
	columns: FormField[],
	maxRows: number,
): { rows: TableRowValue[]; dropped: number; skippedEmpty: number } {
	const lines = tsv.replace(/\r\n?/g, "\n").split("\n");
	const all: TableRowValue[] = [];
	let skippedEmpty = 0;
	for (const line of lines) {
		if (line.trim() === "") continue;
		const cells = line.split("\t");
		const row: TableRowValue = {};
		let hasValue = false;
		columns.forEach((col, i) => {
			const raw = cells[i] ?? "";
			const val = coerceCellForColumn(col, raw);
			row[col.id] = val;
			if (!isCellValueEmpty(val)) hasValue = true;
		});
		if (hasValue) all.push(row);
		else skippedEmpty++;
	}
	const fit = all.slice(0, Math.max(1, maxRows));
	return { rows: fit, dropped: all.length - fit.length, skippedEmpty };
}

/**
 * Отформатировать значение ячейки для человекочитаемого вывода
 * (email/webhook/аналитика). Checkbox → «✓», пусто → «».
 */
export function formatCellForDisplay(value: any): string {
	if (value === undefined || value === null || value === "") {
		return "";
	}
	if (typeof value === "boolean") {
		return value ? "✓" : "";
	}
	if (Array.isArray(value)) {
		return value.map((v) => formatCellForDisplay(v)).filter(Boolean).join(", ");
	}
	return String(value);
}

/**
 * Сжатый текст по строкам для email/webhook (t_82191af7), оба режима:
 * - динамический: каждая непустая строка → «Колонка: значение; …»;
 * - фиксированный: каждая строка → «Метка строки: Колонка: значение; …».
 */
export function formatTableValue(field: FormField, value: any): string {
	const columns = (field.settings?.tableColumns as FormField[] | undefined) || [];
	const fixedRows = field.settings?.tableFixedRows || [];
	if (fixedRows.length > 0) {
		return fixedRows
			.map((r) => {
				const cells = (value?.[r.id] as TableRowValue | undefined) || {};
				const parts = columns
					.filter((col) => !isCellValueEmpty(cells[col.id]))
					.map(
						(col) =>
							`${col.label || col.id}: ${formatCellForDisplay(cells[col.id])}`,
					);
				return `${r.label}: ${parts.join("; ") || "—"}`;
			})
			.join("\n");
	}
	const rows = normalizeTableRows(value, columns);
	if (rows.length === 0) {
		return "";
	}
	return rows
		.map((row) =>
			columns
				.filter((col) => !isCellValueEmpty(row[col.id]))
				.map(
					(col) =>
						`${col.label || col.id}: ${formatCellForDisplay(row[col.id])}`,
				)
				.join("; "),
		)
		.join("\n");
}
